import { lazy } from "react";
import { LAMA_MODULE_BASE } from "@playbookai/types";
import type { ModuleManifest } from "@playbookai/ui";
import { moduleIcon } from "./launcher-entries.js";
import { isModuleEnabled } from "../config/runtime.js";

// ADR-0100's one shell rule, defined beside the published availability seam.
export { isModuleAvailable, moduleStatus } from "./availability.js";

/**
 * Registry of all PlaybookAI modules the shell knows how to mount.
 *
 * Each module package (`packages/<module>-feature/`) exports its own
 * `ModuleManifest`. The shell imports them here and filters down to the
 * enabled set at boot using the runtime config. Adding a module is one new
 * import + one new entry — no central refactor.
 *
 * In MVP, SOLVE, Hoshin, VSM, and LAMA ship real feature packages. DPM remains
 * a placeholder route, so accidental deep links do not hard-404 if a customer
 * enables it in `config.js`.
 */
const SolveFeature = lazy(async () => import("@playbookai/solve-feature"));
const VsmFeature = lazy(async () => import("./VsmWithConfig.js"));
// Hoshin is mounted through a wrapper that injects the tenant people
// directory, so a plan card can name its owner rather than state that it has
// one. See `HoshinWithDirectory`.
const HoshinFeature = lazy(async () => import("./HoshinWithDirectory.js"));
const LamaFeature = lazy(async () => import("@playbookai/lama-feature"));
const KnowledgeStudioFeature = lazy(
  async () => import("./KnowledgeStudioWithAccess.js"),
);
const DpmFeature = lazy(
  async () => import("./placeholders/DpmModulePlaceholder.js"),
);

export const ALL_MODULES: readonly ModuleManifest[] = [
  {
    id: "solve",
    label: "SOLVE",
    routeBase: "/app/solve",
    icon: moduleIcon("solve"),
    Component: SolveFeature,
  },
  {
    id: "vsm",
    label: "VSM",
    routeBase: "/app/vsm",
    entityRouteDepth: 2,
    icon: moduleIcon("vsm"),
    Component: VsmFeature,
  },
  {
    id: "hoshin",
    label: "Hoshin",
    routeBase: "/app/hoshin",
    // A plan lives one segment under the base (`/app/hoshin/<planId>`) — the
    // grid is the bare base, so the two never collide. Declared so the
    // deep-link bootstrap reconstructs a plan as its own entity tab.
    entityRouteDepth: 1,
    icon: moduleIcon("hoshin"),
    Component: HoshinFeature,
  },
  {
    id: "dpm",
    label: "DPM",
    routeBase: "/app/dpm",
    icon: moduleIcon("dpm"),
    Component: DpmFeature,
  },
  {
    id: "lama",
    label: "LAMA",
    routeBase: LAMA_MODULE_BASE,
    icon: moduleIcon("lama"),
    Component: LamaFeature,
  },
  {
    id: "knowledge_studio",
    label: "Knowledge Studio",
    routeBase: "/app/knowledge-studio",
    icon: moduleIcon("knowledge_studio"),
    Component: KnowledgeStudioFeature,
  },
];

/**
 * Filter the registry down to the modules enabled by the runtime config.
 * Consumers: the router (mounts these routes), `AppLauncher` (lists these
 * in the launcher), and any Home widget that aggregates per-module data.
 */
export function getEnabledModules(
  tenantEnabledModules?: readonly string[],
): readonly ModuleManifest[] {
  const tenantEnabled = tenantEnabledModules
    ? new Set(tenantEnabledModules)
    : null;
  /* Every shell module id is also its Auth key, so the claim check needs no
     translation here; KPI, the one module whose keys differ, has no ModuleId. */
  return ALL_MODULES.filter(
    (module) =>
      isModuleEnabled(module.id) &&
      (tenantEnabled === null || tenantEnabled.has(module.id)),
  );
}
